import { GroupCondition } from '@/api/enums/conditions.enum';
import { equal } from '@/api/helpers';
import { group } from '@/api/helpers/filter.helper';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { SurveyPreviewModal } from '@/components/security-culture/modals/SurveyPreviewModal';
import { useQuizzes } from '@/hooks/query/quizzes.hooks';
import { useSurveys } from '@/hooks/query/security-culture.hooks';
import { useAppSelector } from '@/hooks/store';
import SimplifiedSCBlankPageImg from '@/images/blank-pages/simplified-sc-blank-page.svg';
import { selectCurrentAccount } from '@/store/features/account';
import { BlankPage } from '@/ui/blank-page/BlankPage';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import { isScPayingCustomer } from '@/utils/helpers';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LoadingPage } from '../LoadingPage';
import { SCPlannerTableView } from './SCPlannerTableView';

const pathItems = (account: Account, t: TranslationFunctionType) => [
  {
    label: account?.name,
    url: !account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('security.culture'),
    url: RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY](),
    template: AppBreadCrumbTemplate,
    className: 'active',
  },
  {
    label: t('security.culture.survey.planner'),
    template: AppBreadCrumbTemplate,
    url: RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY_PLANNER](),
  },
];

export const SCPlannerPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const isPaying = isScPayingCustomer(currentAccount);

  const [previewQuizId, setPreviewQuizId] = useState<string | undefined>(
    undefined,
  );

  const { surveys, isLoading } = useSurveys(
    { take: 1, filters: [equal('isDemo', false)] },
    currentAccount?.id,
  );

  const { quizzes } = useQuizzes(currentAccount?.id as string, {
    filters: [
      group(GroupCondition.OR, [
        equal('account', currentAccount?.id),
        equal('account', null),
      ]),
    ],
    enabled: !!currentAccount?.id,
  });

  const masterQuiz = quizzes?.result?.find((q) => q.account === null);
  const accountCloneQuiz = quizzes?.result?.find((q) => q.account !== null);

  const hasSurveys = !isLoading && (surveys?.count ?? 0) > 0;

  const handleSchedule = () => {
    navigate(
      RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SEND_SURVEY_V2](),
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return <LoadingPage message={t('security.culture.planner.loading')} />;
    }

    if (!isPaying && hasSurveys) {
      return <SCPlannerTableView baselineUsed />;
    }

    if (!isPaying) {
      return (
        <BlankPage
          image={SimplifiedSCBlankPageImg}
          title={t('security.culture.planner.free.trial.heading')}
          description={t('security.culture.planner.free.trial.description')}
          mainCtaText={t('security.culture.planner.free.trial.schedule.button')}
          mainCtaOnClick={handleSchedule}
          secondaryCtaText={t('security.culture.planner.preview.survey')}
          secondaryCtaOnClick={() =>
            setPreviewQuizId(accountCloneQuiz?.id ?? masterQuiz?.id)
          }
          secondaryBtnType="outlined"
          secondaryBtnSeverity="no-background"
        />
      );
    }

    if (!hasSurveys) {
      return (
        <BlankPage
          image={SimplifiedSCBlankPageImg}
          title={t('security.culture.planner.paying.first.heading')}
          description={t('security.culture.planner.paying.first.description')}
          mainCtaText={t('security.culture.planner.schedule.survey')}
          mainCtaOnClick={handleSchedule}
          secondaryCtaText={t('security.culture.planner.preview.survey')}
          secondaryCtaOnClick={() =>
            setPreviewQuizId(accountCloneQuiz?.id ?? masterQuiz?.id)
          }
          secondaryBtnType="outlined"
          secondaryBtnSeverity="no-background"
        />
      );
    }

    return <SCPlannerTableView />;
  };

  return (
    <FlexContainer
      direction="column"
      align="flex-start"
      justify="flex-start"
      minHeight="100%"
      flex="1"
    >
      <AppBreadCrumb model={pathItems(currentAccount as Account, t)} />
      <h1 className="mb-5">{t('security.culture.survey.planner')}</h1>
      {renderContent()}
      <SurveyPreviewModal
        visible={!!previewQuizId}
        onHide={() => setPreviewQuizId(undefined)}
        surveyId={previewQuizId}
      />
    </FlexContainer>
  );
};
